/**
 * 共享 Google GSI 初始化与 One Tap，供主页面与 login-box 使用，保证整页只 initialize 一次。
 */
import store from '@/store'
import { $loading, $notification, $t } from '@/utils'
import { globalEmit } from '@/utils/eventBus'
import { EVENT_TYPES } from '@/utils/eventBus'

function getGoogleSDK() {
    return (window as any).google?.accounts?.id
}

export function decodeGoogleCredential(credential: string): { email?: string; picture?: string; name?: string; sub?: string } | null {
    try {
        const base64Url = credential.split('.')[1]
        if (!base64Url) return null
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(char => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join('')
        )
        return JSON.parse(jsonPayload)
    } catch {
        return null
    }
}

let gisInitialized = false
let hasPromptedOneTap = false
let currentRedirectType = ''

async function handleCredentialResponse(response: { credential?: string }) {
    $loading.loading()
    try {
        const credential = response?.credential
        if (!credential) {
            $notification.error($t('login.googleLoginError'))
            return
        }
        const googleUserInfo = decodeGoogleCredential(credential)
        if (!googleUserInfo?.email || !googleUserInfo?.sub) {
            $notification.error($t('login.googleLoginError'))
            return
        }
        await store.dispatch('user/googleLogin', {
            googleUserInfo: {
                email: googleUserInfo.email,
                picture: googleUserInfo.picture || '',
                name: googleUserInfo.name || googleUserInfo.email,
                id: googleUserInfo.sub
            }
        })
        globalEmit(EVENT_TYPES.LOGIN_SUCCESS, currentRedirectType)
    } catch (error: any) {
        try {
            const errorData = JSON.parse(error.message)
            $notification.error($t('login.googleLoginError'), errorData.error)
        } catch {
            $notification.error($t('login.googleLoginError'))
        }
    } finally {
        $loading.hide()
    }
}

/**
 * 确保 GSI 已加载并只初始化一次，供 login-box 与主页面共用。
 */
export async function ensureInitialized(): Promise<void> {
    const sdk = getGoogleSDK()
    if (!sdk || typeof sdk.initialize !== 'function') {
        throw new Error('google sdk unavailable')
    }
    if (!gisInitialized) {
        sdk.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            cancel_on_tap_outside: false
        })
        gisInitialized = true
    }
}

/**
 * 显示 One Tap 提示条。主页面或登录框调用前需先 ensureInitialized()。
 * @param redirectType 登录成功后的跳转类型
 * @param force 为 true 时在登录框打开时也强制显示（不检查是否已展示过）
 */
export function promptOneTap(redirectType: string, force = false): void {
    const sdk = getGoogleSDK()
    if (!sdk || typeof sdk.prompt !== 'function') return
    if (!force && hasPromptedOneTap) return
    currentRedirectType = redirectType
    hasPromptedOneTap = true
    sdk.prompt()
}

export function getGoogleSDKExport() {
    return getGoogleSDK()
}

export function cancelOneTap(): void {
    const sdk = getGoogleSDK()
    if (sdk && typeof sdk.cancel === 'function') {
        sdk.cancel()
    }
}

/** 设置当前登录成功后的 redirect 类型（如从登录框内点击按钮时使用） */
export function setRedirectType(redirectType: string): void {
    currentRedirectType = redirectType
}
